import React, { useContext } from 'react'

import { Form, Checkbox, CheckboxProps } from 'semantic-ui-react'
import { Typography, Box, Card, CardContent, Alert } from '@mui/material'
import CropLandscapeOutlinedIcon from '@mui/icons-material/CropLandscapeOutlined'
import CropPortraitOutlinedIcon from '@mui/icons-material/CropPortraitOutlined'
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined'

import CustomLabel from '../../../components/common/custom-label/CustomLabel'
import UpgradeTag from '../../../components/common/UpgradeTag'
import { UserContext } from '../../../contexts/UserContext'

import { IProject } from '../../../api/Data'

const helperTextStyle = {
  color: '#6b7280',
  display: 'block',
  lineHeight: 1.4,
  marginTop: '0.25rem',
}

const limitHelperTextStyle = {
  ...helperTextStyle,
  minHeight: '4.5rem',
  overflowWrap: 'anywhere' as const,
}

const formatTileStyle = {
  alignItems: 'center',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  minHeight: '36px',
  minWidth: 0,
  padding: '0.45rem 0.5rem',
  width: '100%',
}

export const formatFileSize = (sizeInMb: number) => {
  if (sizeInMb < 1024) return `${sizeInMb} MB`

  const sizeInGb = sizeInMb / 1024
  return `${Number.isInteger(sizeInGb) ? sizeInGb : sizeInGb.toFixed(1)} GB`
}

const FormatsCard = (props: IProps) => {
  const { project, handleChange } = props
  const { userProfile } = useContext(UserContext)

  const { allowRecordVideo, allowUploadFile, allowQrCode } = project
  const isConversationWorkflow = project.projectType === 'video_conversation'
  const isMultiFileWorkflow = project.projectType === 'video_collection'
  const isContributionWorkflow = project.projectType === 'video_contribution'
  const isFileCountWorkflow = isContributionWorkflow || isMultiFileWorkflow
  const { isAllowedBatchUpload, maxBatchFiles } = userProfile.plan
  const maxSelectableFiles = Math.min(8, Math.max(2, maxBatchFiles || 2))
  const selectedFileCount = isMultiFileWorkflow ? Math.max(2, project.maxFilesPerSubmission || 2) : 1
  const canChangeFileCount = isAllowedBatchUpload && isFileCountWorkflow
  const acceptedFileTypes =
    Array.isArray(project.acceptedUploadFileTypes) && project.acceptedUploadFileTypes.length > 0
      ? project.acceptedUploadFileTypes
      : ['mp4', 'avi', 'mov', 'webm']
  const maxFileSize = userProfile.maxSizeOfClipInMb
  const readOnlyInputStyle = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    '& .field': { marginBottom: 0 },
    '& .field > label': { fontSize: '0.75rem' },
    '& .ui.disabled.input': { opacity: 1 },
    '& input': {
      backgroundColor: '#f3f4f6 !important',
      color: '#6b7280 !important',
      fontSize: '0.8125rem',
      height: '38px',
      padding: '0.65rem 0.75rem',
    },
  }

  const handleCheckboxChange = (e: React.MouseEvent<HTMLInputElement, MouseEvent>, data: CheckboxProps) => {
    const { name, checked } = data

    if (name === 'allowUploadFile' && isConversationWorkflow) return

    handleChange(e, {
      name: String(name),
      checked: Boolean(checked),
    })
  }

  const handleFileCountChange = (event: React.ChangeEvent<HTMLInputElement>, data: { value?: string | number }) => {
    const nextFileCount = Math.min(maxSelectableFiles, Math.max(1, Number(data.value) || 1))
    const nextProjectType = nextFileCount > 1 ? 'video_collection' : 'video_contribution'

    handleChange(event, {
      updates: {
        projectType: nextProjectType,
        maxFilesPerSubmission: nextFileCount,
        allowUploadFile: nextFileCount > 1 ? true : project.allowUploadFile,
        allowBatchUpload: nextFileCount > 1,
      },
    })
  }

  return (
    <Card sx={{ width: '100%', boxSizing: 'border-box' }}>
      <CardContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="h5" fontWeight="bold">
            Upload Formats
          </Typography>

          <Form>
            <Form.Field>
              <CustomLabel>
                <CustomLabel.Text text="Device Camera" />
                <CustomLabel.Popup popupContent="Allow contributors to record videos directly from their device camera" />
                <CustomLabel.Checkbox
                  name="allowRecordVideo"
                  checked={!!allowRecordVideo}
                  onClick={handleCheckboxChange}
                />
              </CustomLabel>
              <small style={helperTextStyle}>Record a response directly from the contributor’s device camera.</small>
            </Form.Field>

            <Box
              sx={{
                backgroundColor: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                marginBottom: '1rem',
                padding: '0.9rem',
              }}
            >
              <Form.Field>
                <CustomLabel>
                  <CustomLabel.Text text="Upload Files" />
                  <CustomLabel.Popup popupContent="Allow contributors to upload one or more prerecorded videos" />
                  <CustomLabel.Checkbox
                    name="allowUploadFile"
                    checked={!isConversationWorkflow && !!allowUploadFile}
                    disabled={isConversationWorkflow}
                    onClick={handleCheckboxChange}
                  />
                </CustomLabel>
                <small style={helperTextStyle}>
                  {isConversationWorkflow
                    ? 'Conversation requires a new camera recording for each prompt.'
                    : 'Allow contributors to select prerecorded files from their device.'}
                </small>
                {isConversationWorkflow && (
                  <Alert severity="info" sx={{ marginTop: 1 }}>
                    To allow prerecorded files, change the Submission Workflow to Contribution or Collection.
                  </Alert>
                )}
              </Form.Field>

              <Box data-testid="file-limit-controls" mb={1.5}>
                <Box
                  data-testid="file-limit-grid"
                  display="grid"
                  gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                  alignItems="start"
                  columnGap={1.5}
                  rowGap={1}
                  mt={2}
                >
                  <Box data-testid="maximum-file-size-field" sx={readOnlyInputStyle}>
                  <Form.Input
                    aria-label="Maximum File Size"
                    label="Maximum File Size"
                    value={formatFileSize(maxFileSize)}
                    disabled
                  />
                  <small style={limitHelperTextStyle}>Set by subscription tier.</small>
                </Box>

                  <Box
                    data-testid="files-per-submission-field"
                    sx={{
                      ...readOnlyInputStyle,
                      '& input': {
                        ...readOnlyInputStyle['& input'],
                        backgroundColor: canChangeFileCount ? '#fff !important' : '#f3f4f6 !important',
                        color: canChangeFileCount ? '#111827 !important' : '#6b7280 !important',
                      },
                    }}
                  >
                  <Form.Input
                    type="number"
                    min={1}
                    max={maxSelectableFiles}
                    label="Files per Submission"
                    name="maxFilesPerSubmission"
                    value={selectedFileCount}
                    disabled={!canChangeFileCount}
                    onChange={handleFileCountChange}
                  />
                  <small style={limitHelperTextStyle}>
                    {canChangeFileCount
                      ? `1 file is a Contribution; 2–${maxSelectableFiles} files make a Collection.`
                      : isFileCountWorkflow
                      ? 'Your subscription supports one file per submission.'
                      : 'Set by the selected submission workflow.'}
                  </small>
                  {isFileCountWorkflow && !isAllowedBatchUpload && (
                    <Box mt={0.75}>
                      <UpgradeTag text="Upgrade your plan to let contributors upload more than one file per submission" />
                    </Box>
                  )}
                  </Box>
                </Box>
              </Box>

              <Form.Field
                data-testid="accepted-file-formats"
                style={{
                  borderTop: '1px solid #e5e7eb',
                  clear: 'both',
                  marginTop: 0,
                  paddingTop: '1rem',
                }}
              >
                <label>Accepted File Formats</label>
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1} mt={0.75}>
                  {['mp4', 'avi', 'mov', 'webm'].map((fileType) => (
                    <Box key={fileType} sx={formatTileStyle}>
                      <Checkbox
                        label={fileType.toUpperCase()}
                        style={{ fontSize: '0.8125rem' }}
                        checked={acceptedFileTypes.includes(fileType)}
                        onChange={(event) => {
                          const nextTypes = acceptedFileTypes.includes(fileType)
                            ? acceptedFileTypes.filter((type) => type !== fileType)
                            : [...acceptedFileTypes, fileType]
                          if (nextTypes.length > 0)
                            handleChange(event, { name: 'acceptedUploadFileTypes', value: nextTypes })
                        }}
                      />
                    </Box>
                  ))}
                  {['MP3', 'JPEG', 'PNG'].map((fileType) => (
                    <Box
                      key={fileType}
                      aria-disabled="true"
                      data-format-status="future"
                      sx={{ ...formatTileStyle, backgroundColor: '#f3f4f6', borderColor: '#e5e7eb', color: '#9ca3af' }}
                    >
                      <Typography fontSize="0.8125rem" fontWeight="bold">
                        {fileType}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <small style={helperTextStyle}>At least one available video format must remain selected.</small>
              </Form.Field>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                Project Video Orientation
              </Typography>
              <Box
                role="group"
                aria-label="Project Video Orientation"
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                gap={1}
              >
                {[
                  { label: 'Portrait', icon: CropPortraitOutlinedIcon, supported: false },
                  { label: 'Square', icon: CropSquareOutlinedIcon, supported: false },
                  { label: 'Landscape', icon: CropLandscapeOutlinedIcon, supported: true },
                ].map(({ label, icon: OrientationIcon, supported }) => (
                  <Box
                    key={label}
                    aria-disabled={!supported}
                    aria-current={supported ? 'true' : undefined}
                    data-orientation={label.toLowerCase()}
                    sx={{
                      alignItems: 'center',
                      backgroundColor: supported ? '#f5f3ff' : '#f3f4f6',
                      border: supported ? '1px solid #6b50ff' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      color: supported ? '#4f36d9' : '#9ca3af',
                      display: 'flex',
                      flexDirection: 'column',
                      fontSize: '0.75rem',
                      gap: 0.5,
                      padding: '0.65rem 0.25rem',
                    }}
                  >
                    <OrientationIcon fontSize="small" />
                    <span>{label}</span>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" mt={0.75}>
                Landscape is currently supported. Portrait and square will be available later.
              </Typography>
            </Box>

            <Form.Field>
              <CustomLabel>
                <CustomLabel.Text text="Display QR Code" />
                <CustomLabel.Popup popupContent="Allow contributors to transfer to another device with a QR code" />
                <CustomLabel.Checkbox name="allowQrCode" checked={!!allowQrCode} onClick={handleCheckboxChange} />
              </CustomLabel>
              <small style={helperTextStyle}>
                Allow contributors to transfer to another device. Not recommended for verified human capture.
              </small>
            </Form.Field>
          </Form>
        </Box>
      </CardContent>
    </Card>
  )
}

interface IProps {
  project: IProject
  handleChange: (arg: React.SyntheticEvent, data: any) => void
}

export default FormatsCard
